// src/public-routes/sectors/FintechSector.jsx
import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaCreditCard,
    FaRocket,
    FaShieldAlt,
    FaMobileAlt,
    FaSync,
    FaUsers,
    FaChartLine,
    FaTrophy
} from 'react-icons/fa';

const FintechSector = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const stats = [
        { number: '$310B', label: 'Global Fintech Market Value by 2025' },
        { number: '64%', label: 'of consumers use fintech products regularly' },
        { number: '4.2x', label: 'higher user acquisition with performance marketing' },
        { number: '72%', label: 'of fintech users prefer mobile-first experiences' }
    ];

    const approaches = [
        {
            title: "User Acquisition at Scale",
            description: "Drive rapid user growth through agile, performance-led campaigns optimized for fintech metrics and user acquisition costs.",
            icon: FaRocket
        },
        {
            title: "Trust & Security Marketing",
            description: "Build credibility and trust with transparent communication, security messaging, and educational content that addresses user concerns.",
            icon: FaShieldAlt
        },
        {
            title: "Mobile-First Engagement",
            description: "Create seamless mobile experiences that drive app installs, user activation, and long-term engagement with your fintech platform.",
            icon: FaMobileAlt
        },
        {
            title: "Retention & Lifecycle Optimization",
            description: "Reduce churn and maximize user lifetime value through personalized engagement, feature adoption campaigns, and loyalty programs.",
            icon: FaSync
        }
    ];

    const benefits = [
        ['Accelerated User Growth', 'Scale your user base rapidly with performance-driven campaigns optimized for fintech metrics'],
        ['Enhanced Trust & Credibility', 'Build the trust essential for financial technology adoption with transparent, compliant marketing'],
        ['Higher User Engagement', 'Drive daily active usage with personalized, behavior-based engagement strategies'],
        ['Reduced Churn Rates', 'Keep users active and retained through intelligent lifecycle marketing and re-engagement campaigns'],
        ['Competitive Market Position', 'Stand out in the crowded fintech space with distinctive, innovation-led marketing']
    ];

    const questions = [
        'How can you scale user acquisition while managing costs?',
        'What strategies will build trust in your financial technology?',
        'How do you drive app installs and user activation?',
        'Which channels deliver the highest quality fintech users?',
        'How can you reduce churn and increase user lifetime value?'
    ];

    return (
        <div className="fintech-sector-page st-page">
            {/* Top Image Section */}
            <div className="st-decorative-image-container">
                <img
                    src={require('./Fintech.PNG')}
                    alt="Fintech Digital Marketing - User Acquisition and Engagement"
                    loading="eager"
                />
            </div>

            {/* Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Fintech</span> Digital Marketing
                    </h1>
                    <p>Speed, trust, and innovation define fintech — our strategies drive user acquisition and long-term engagement</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Speed, trust, and innovation define <span className="sv-keyword-highlight">fintech</span> — and our strategies reflect that. We support fintech brands with agile, performance-led campaigns that drive user acquisition and long-term engagement.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>fintech marketing services</strong> combine strategic thinking, data intelligence, and advanced tools to create campaigns that build trust, drive adoption, and scale user growth. We understand the unique balance between innovation and security that fintech demands.
                                </p>
                                <p className="sv-description">
                                    We understand the unique challenges fintech companies face: building trust in new financial technologies, competing with traditional institutions, navigating regulatory requirements, and scaling user acquisition while maintaining strong unit economics.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Fintech Strategy
                                    </Button>
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Fintech Marketing</span> Matters
                        </h2>
                        <Row className="sv-stats-grid">
                            {stats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What We Do Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Do for Fintech Companies</h2>
                                <p className="sv-section-description">
                                    <strong>Fintech marketing</strong> requires a unique approach that balances rapid growth with trust-building. It's about creating campaigns that communicate innovation while establishing the security and credibility essential for financial technology adoption.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions We Answer:</h4>
                                    {questions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Overview */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in Fintech Marketing?</h2>
                                <p className="sv-section-description">
                                    The fintech revolution is reshaping financial services. A comprehensive digital marketing strategy is essential to build trust, drive adoption, and scale your user base in an increasingly competitive and regulated landscape.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-rocket"></i>
                                        <h5>Rapid Scaling</h5>
                                        <p>Accelerate user growth with performance-optimized acquisition campaigns</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Trust Building</h5>
                                        <p>Establish credibility essential for financial technology adoption</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-mobile-alt"></i>
                                        <h5>Mobile Engagement</h5>
                                        <p>Drive app installs and daily active usage with mobile-first strategies</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-sync"></i>
                                        <h5>User Retention</h5>
                                        <p>Keep users engaged with intelligent lifecycle marketing campaigns</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Fintech Marketing Services</h2>
                                <p className="sv-section-description">
                                    Reachtix offers a comprehensive suite of fintech marketing services designed to drive user acquisition, build trust, and maximize lifetime value in the fast-paced financial technology sector.
                                </p>

                                <Row className="sv-services-grid">
                                    {approaches.map((service, index) => (
                                        <Col lg={6} md={6} key={index} className="mb-4">
                                            <div className="sv-approach-card p-4 h-100">
                                                <div className="sv-approach-icon">
                                                    <service.icon />
                                                </div>
                                                <div className="sv-approach-content">
                                                    <h4>{service.title}</h4>
                                                    <p className="service-description">{service.description}</p>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Table Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of Fintech Marketing With Reachtix</h2>
                                <div className="table-responsive">
                                    <table className="table sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Main CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Scale Your Fintech Platform?</h2>
                                <p className="sv-cta-description">
                                    Whether you're a disruptive startup challenging traditional banking or an established fintech platform scaling to new markets, our comprehensive marketing services provide the agility and expertise you need to grow.
                                </p>
                                <p className="sv-cta-description">
                                    Let's build a performance-driven marketing strategy that accelerates user acquisition and builds lasting engagement.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="primary">
                                        <i className="fas fa-calendar-check mr-2"></i>
                                        Start Fintech Consultation
                                    </Button>
                                    <Button tag={Link} to="/contact" color="light">
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get Growth Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default FintechSector;